// src/components/Modals/KilnLogModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useNavigate, useLocation } from "react-router-dom";

export default function KilnLogModal({ onClose, batchOptions = [], initialData = null }) {
  const { play, buzz } = useFeedback();
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);

  const [id, setId] = useState(initialData?.id || null);
  const [date, setDate] = useState("");
  const [type, setType] = useState(initialData?.type || "Bisque");
  const [temperature, setTemperature] = useState(
    Number.isFinite(initialData?.temperature) ? Number(initialData.temperature) : 1000
  );
  const [durationHours, setDurationHours] = useState(
    Number.isFinite(initialData?.durationHours) ? Number(initialData.durationHours) : 8
  );
  const [notes, setNotes] = useState(initialData?.notes || "");
  // keep as string across UI & storage
  const [batchId, setBatchId] = useState(
    initialData?.batchId != null ? String(initialData.batchId) : ""
  );
  // align with table vocabulary
  const [status, setStatus] = useState(initialData?.status || "scheduled");
  const [errors, setErrors] = useState({});

  // id -> name map
  const batchMap = useMemo(() => {
    const m = new Map();
    (batchOptions || []).forEach((b) => m.set(String(b.id), b.name || `Batch ${b.id}`));
    return m;
  }, [batchOptions]);

  // init date + preselect batch from ?batchId=
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDate(initialData?.date || today);

    if (!initialData?.batchId) {
      const qs = new URLSearchParams(location.search);
      const qBatch = qs.get("batchId");
      if (qBatch) setBatchId(String(qBatch));
    }
  }, [initialData, location.search]);

  // keyboard shortcuts + autofocus
  useEffect(() => {
    const first = rootRef.current?.querySelector?.("input,select,textarea,button");
    first?.focus?.();

    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        play("tap"); buzz("light");
        onClose(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "enter") {
        e.preventDefault();
        handleSave();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, type, temperature, durationHours, notes, status, batchId]);

  function validate() {
    const e = {};
    if (!date) e.date = "Required";
    if (!type) e.type = "Required";
    if (!Number.isFinite(Number(temperature))) e.temperature = "Enter a number";
    if (!Number.isFinite(Number(durationHours))) e.durationHours = "Enter a number";
    if (Number(temperature) < 200 || Number(temperature) > 1320) {
      e.temperature = "Typical range 200–1320°C";
    }
    if (Number(durationHours) <= 0) e.durationHours = "Must be greater than 0";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const handleSave = () => {
    if (!validate()) {
      play("error"); buzz("error");
      return;
    }
    const storageKey = "mudoKilnLogs";
    const saved = JSON.parse(localStorage.getItem(storageKey) || "[]");

    const log = {
      id: id || Date.now().toString(),
      date,
      type,
      temperature: Number(temperature),
      durationHours: Number(durationHours),
      notes,
      batchId: batchId ? String(batchId) : null,
      status,
    };

    const next = id ? saved.map((l) => (l.id === id ? log : l)) : [...saved, log];
    localStorage.setItem(storageKey, JSON.stringify(next));
    play("success"); buzz("success");
    onClose(true);
  };

  const handleCancel = () => {
    play("tap"); buzz("light");
    onClose(false);
  };

  const selectedBatchName = batchId ? batchMap.get(String(batchId)) : null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4"
      role="dialog" aria-modal="true" aria-labelledby="kilnlog-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(false); }}
    >
      <div
        ref={rootRef}
        className="bg-zinc-800 text-white p-6 rounded shadow-lg w-full max-w-md space-y-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="kilnlog-title" className="text-xl font-bold">
          {id ? "Edit Firing Log" : "Log New Kiln Firing"}
        </h2>

        {/* Date */}
        <label className="block">Date</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          onBlur={() => play("tap")}
          className="w-full p-2 rounded text-black"
        />
        {errors.date && <p className="text-xs text-red-300">{errors.date}</p>}

        {/* Type */}
        <label className="block">Firing Type</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="w-full p-2 rounded text-black"
        >
          <option>Bisque</option>
          <option>Glaze</option>
          <option>Other</option>
        </select>
        {errors.type && <p className="text-xs text-red-300">{errors.type}</p>}

        {/* Temperature */}
        <label className="block">Temperature (°C)</label>
        <input
          type="number"
          inputMode="numeric"
          value={temperature}
          onChange={(e) => setTemperature(e.target.value)}
          onBlur={() => play("tap")}
          className="w-full p-2 rounded text-black"
          min={200}
          max={1320}
        />
        {errors.temperature && <p className="text-xs text-red-300">{errors.temperature}</p>}

        {/* Duration */}
        <label className="block">Duration (hours)</label>
        <input
          type="number"
          inputMode="numeric"
          value={durationHours}
          onChange={(e) => setDurationHours(e.target.value)}
          onBlur={() => play("tap")}
          className="w-full p-2 rounded text-black"
          min={0.1}
          step="0.1"
        />
        {errors.durationHours && <p className="text-xs text-red-300">{errors.durationHours}</p>}

        {/* Notes */}
        <label className="block">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full p-2 rounded text-black"
          rows={3}
        />

        {/* Batch Link */}
        <label className="block">Link to Batch (optional)</label>
        <select
          value={batchId || ""}
          onChange={(e) => setBatchId(e.target.value)}
          className="w-full p-2 rounded text-black"
        >
          <option value="">-- None --</option>
          {batchOptions.map((b) => (
            <option key={b.id} value={String(b.id)}>
              {b.name}
            </option>
          ))}
        </select>

        {selectedBatchName && (
          <div className="flex items-center justify-between text-xs opacity-80">
            <div>
              Linked to: <span className="font-medium">{selectedBatchName}</span>
            </div>
            <button
              type="button"
              className="underline hover:opacity-80"
              onClick={() => navigate(`/batches/${batchId}`)}
              title="Open batch"
            >
              Open batch →
            </button>
          </div>
        )}

        {/* Status */}
        <label className="block">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full p-2 rounded text-black"
        >
          <option value="scheduled">scheduled</option>
          <option value="fired">fired</option>
          <option value="completed">completed</option>
        </select>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button onClick={handleCancel} className="bg-gray-600 px-4 py-2 rounded">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-green-600 px-4 py-2 rounded"
            title="⌘/Ctrl+Enter"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
